import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  RotateCcw, 
  Loader2, 
  AlertCircle, 
  User, 
  FileText 
} from 'lucide-react';
import { useStore } from '../../hooks/useStore.js';
import { getBorrowUnreturnedQty } from '../../forms/return-form.js';
import { addTransaction, updateInventoryItem } from '../../state/store.js';
import { saveLocalData } from '../../state/storage.js';
import { syncAfterLocalChange } from '../../services/sync.js';
import { getAuthenticatedOperatorName, getAuthenticatedOperatorEmail } from '../../services/auth.js';
import { hasCloudSyncConfig } from '../../services/supabase.js';
import { rpcReturnInventory } from '../../services/rpc.js';

export function ReturnModal({ 
  isOpen, 
  preselectedBorrowTxId = null, 
  onClose, 
  onSuccess 
}) {
  const transactions = useStore(s => s.transactions) || [];
  const inventory = useStore(s => s.inventory) || [];
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [selectedBorrowId, setSelectedBorrowId] = useState('');
  const [returnQty, setReturnQty] = useState(1);
  const [returnedBy, setReturnedBy] = useState('');
  const [condition, setCondition] = useState('สภาพปกติสมบูรณ์');
  const [notes, setNotes] = useState('');

  // Unreturned borrows
  const unreturnedBorrows = useMemo(() => {
    return transactions
      .filter(t => !t.deleted && t.type === 'borrow')
      .map(bt => {
        const unreturned = getBorrowUnreturnedQty(bt);
        return { ...bt, unreturnedQty: unreturned };
      })
      .filter(bt => bt.unreturnedQty > 0);
  }, [transactions]);

  const activeBorrowTx = useMemo(() => {
    return unreturnedBorrows.find(b => b.id === selectedBorrowId) || null;
  }, [unreturnedBorrows, selectedBorrowId]);

  useEffect(() => {
    if (!isOpen) return;

    const defaultOperator = getAuthenticatedOperatorName() || getAuthenticatedOperatorEmail() || 'เจ้าหน้าที่คลัง SDR';
    setReturnedBy(defaultOperator);
    setCondition('สภาพปกติสมบูรณ์');
    setNotes('');
    setErrorMsg('');

    if (preselectedBorrowTxId) {
      setSelectedBorrowId(preselectedBorrowTxId);
      const b = unreturnedBorrows.find(x => x.id === preselectedBorrowTxId);
      setReturnQty(b ? b.unreturnedQty : 1);
    } else if (unreturnedBorrows.length > 0) {
      setSelectedBorrowId(unreturnedBorrows[0].id);
      setReturnQty(unreturnedBorrows[0].unreturnedQty);
    } else {
      setSelectedBorrowId('');
      setReturnQty(1);
    }
  }, [isOpen, preselectedBorrowTxId, unreturnedBorrows]);

  if (!isOpen) return null;

  const handleSelectBorrow = (borrowId) => {
    setSelectedBorrowId(borrowId);
    const b = unreturnedBorrows.find(x => x.id === borrowId);
    if (b) setReturnQty(b.unreturnedQty);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeBorrowTx) {
      setErrorMsg('กรุณาเลือกรายการยืมที่ต้องการคืน');
      return;
    }

    const qty = Number(returnQty) || 0;
    if (qty <= 0) {
      setErrorMsg('กรุณาระบุจำนวนที่ต้องการคืนให้มากกว่า 0');
      return;
    }

    if (qty > activeBorrowTx.unreturnedQty) {
      setErrorMsg(`จำนวนคืน (${qty}) เกินกว่าจำนวนที่คงค้างการยืม (${activeBorrowTx.unreturnedQty})`);
      return;
    }

    setSaving(true);
    setErrorMsg('');

    try {
      const nowIso = new Date().toISOString();
      const operatorEmail = getAuthenticatedOperatorEmail() || 'user';

      // Online RPC attempt
      if (hasCloudSyncConfig()) {
        try {
          await rpcReturnInventory(activeBorrowTx.id, qty, condition, notes);
        } catch (rpcErr) {
          console.warn('RPC return fallback to local transaction:', rpcErr);
        }
      }

      // Record return transaction
      const returnTxId = `tx_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      addTransaction({
        id: returnTxId,
        itemId: activeBorrowTx.itemId,
        itemName: activeBorrowTx.itemName,
        type: 'return',
        quantity: qty,
        unit: activeBorrowTx.unit || 'ชิ้น',
        borrowerName: returnedBy.trim() || activeBorrowTx.borrowerName,
        borrowTxId: activeBorrowTx.id,
        notes: `[ยืม: ${activeBorrowTx.id}] คืนสภาพ: ${condition} ${notes ? `(${notes})` : ''}`,
        timestamp: nowIso,
        operatorEmail: operatorEmail,
        deleted: false
      });

      // Restock lot quantity
      const targetLot = inventory.find(i => i.id === activeBorrowTx.itemId);
      if (targetLot) {
        updateInventoryItem({
          ...targetLot,
          quantity: Number(((Number(targetLot.quantity) || 0) + qty).toFixed(5)),
          updatedAt: nowIso,
          updatedBy: operatorEmail
        });
      }

      saveLocalData();
      syncAfterLocalChange('return-transaction');

      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      setErrorMsg(err.message || 'เกิดข้อผิดพลาดในการบันทึกการคืน');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-150 my-6">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-emerald-50/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-800">
              <RotateCcw className="w-4 h-4" />
            </div>
            <h2 className="text-base font-bold text-slate-900">บันทึกการคืนพัสดุ</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          {errorMsg && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {unreturnedBorrows.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">
              ไม่มีรายการพัสดุที่ค้างคืนอยู่ในระบบขณะนี้
            </div>
          ) : (
            <>
              {/* Select Borrow Session */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  เลือกรายการที่ต้องการคืน <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedBorrowId}
                  onChange={(e) => handleSelectBorrow(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                >
                  {unreturnedBorrows.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.itemName} (ค้างคืน {b.unreturnedQty} {b.unit}) - ผู้ยืม: {b.borrowerName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quantity to return */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    จำนวนที่นำมาคืน <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={activeBorrowTx ? activeBorrowTx.unreturnedQty : 9999}
                    required
                    value={returnQty}
                    onChange={(e) => setReturnQty(e.target.value)}
                    className="w-full px-3 py-2 text-sm font-extrabold text-center rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    สภาพพัสดุ
                  </label>
                  <select
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  >
                    <option value="สภาพปกติสมบูรณ์">สภาพปกติสมบูรณ์</option>
                    <option value="มีรอยขีดข่วนเล็กน้อย">มีรอยขีดข่วนเล็กน้อย</option>
                    <option value="ชำรุดรอซ่อม">ชำรุดรอซ่อม</option>
                    <option value="สูญหาย/ใช้หมดไป">สูญหาย/ใช้หมดไป</option>
                  </select>
                </div>
              </div>

              {/* Returned By */}
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  ชื่อผู้ส่งคืน
                </label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="text"
                    value={returnedBy}
                    onChange={(e) => setReturnedBy(e.target.value)}
                    placeholder="ชื่อผู้ส่งคืน"
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  หมายเหตุเพิ่มเติม
                </label>
                <div className="relative">
                  <FileText className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                  <textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="ระบุรายละเอียดเพิ่มเติม"
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-xs transition-colors cursor-pointer"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={saving || unreturnedBorrows.length === 0}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs shadow-xs transition-colors cursor-pointer"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <span>{saving ? 'กำลังประมวลผล...' : 'ยืนยันการคืนพัสดุ'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
